import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { useWorkspace, useUpdateWorkspace } from "../../hooks/useWorkspace";
import {
  useNotificationPrefs,
  useUpdateNotificationPrefs,
  type NotificationDigestCadence,
  type PerEventToggles,
} from "../../hooks/useNotificationPrefs";
import {
  useTeamsWebhook,
  useUpdateTeamsWebhook,
} from "../../hooks/useChannelSettings";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPut, apiDelete, apiErrorMessage } from "../../api/client";
import { ReportRecipients } from "../../components/ReportRecipients";

function SettingsInner() {
  const { workspaceId = "" } = useParams<{ workspaceId: string }>();
  const ws = useWorkspace(workspaceId);
  const update = useUpdateWorkspace(workspaceId);

  const [name, setName] = useState("");
  const [cxoRoleTag, setCxoRoleTag] = useState("");

  useEffect(() => {
    if (ws.data?.workspace) {
      setName(ws.data.workspace.name);
      setCxoRoleTag(ws.data.workspace.cxoRoleTag);
    }
  }, [ws.data?.workspace]);

  const isCxo = ws.data?.currentUserRole === "cxo";
  const dirty =
    !!ws.data?.workspace &&
    (name !== ws.data.workspace.name ||
      cxoRoleTag !== ws.data.workspace.cxoRoleTag);

  async function handleSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!dirty) return;
    const patch: { name?: string; cxoRoleTag?: string } = {};
    if (name !== ws.data?.workspace.name) patch.name = name;
    if (cxoRoleTag !== ws.data?.workspace.cxoRoleTag) {
      patch.cxoRoleTag = cxoRoleTag;
    }
    await update.mutateAsync(patch).catch(() => {
      /* surfaced via mutation state */
    });
  }

  return (
    <div className="main-inner" data-testid="settings">
      <div className="greet-row">
        <span className="greet">Settings</span>
        <span className="greet-date">{isCxo ? "WORKSPACE" : "YOU"}</span>
      </div>
      {isCxo ? (
        <>
      <div className="subgreet">
        <div className="section-eyebrow">Workspace identity</div>
        <div className="lede">Update the department name and supervisor role tag.</div>
      </div>
      <form
        onSubmit={handleSave}
        data-testid="settings-form"
        style={{
          marginTop: 18,
          padding: 20,
          border: "1px solid var(--border)",
          borderRadius: 8,
          background: "white",
          maxWidth: 540,
        }}
      >
        <label
          htmlFor="ws-name"
          style={{
            display: "block",
            fontSize: 11,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "var(--text-3)",
            fontWeight: 600,
            marginBottom: 6,
          }}
        >
          Workspace name
        </label>
        <input
          id="ws-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!isCxo}
          required
          maxLength={120}
          data-testid="settings-name"
          style={{
            width: "100%",
            padding: "8px 10px",
            border: "1px solid var(--border)",
            borderRadius: 6,
            fontSize: 13,
            marginBottom: 16,
          }}
        />

        <label
          htmlFor="ws-tag"
          style={{
            display: "block",
            fontSize: 11,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "var(--text-3)",
            fontWeight: 600,
            marginBottom: 6,
          }}
        >
          Supervisor role tag
        </label>
        <input
          id="ws-tag"
          type="text"
          value={cxoRoleTag}
          onChange={(e) => setCxoRoleTag(e.target.value)}
          disabled={!isCxo}
          required
          maxLength={40}
          data-testid="settings-cxo-tag"
          placeholder="e.g. Head of Savings"
          style={{
            width: "100%",
            padding: "8px 10px",
            border: "1px solid var(--border)",
            borderRadius: 6,
            fontSize: 13,
            marginBottom: 16,
          }}
        />

        {isCxo ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              type="submit"
              className="btn primary"
              disabled={!dirty || update.isPending}
              data-testid="settings-save"
            >
              {update.isPending ? "Saving…" : "Save changes"}
            </button>
            {update.isError ? (
              <span
                data-testid="settings-error" style={{ color: "var(--warn)" }}
              >
                {update.error instanceof Error
                  ? update.error.message
                  : "Save failed"}
              </span>
            ) : null}
            {update.isSuccess && !dirty ? (
              <span
                data-testid="settings-success" style={{ color: "var(--accent)" }}
              >
                Saved.
              </span>
            ) : null}
          </div>
        ) : null}
      </form>
        </>
      ) : (
        // Everyone else gets their own settings and none of the workspace's.
        // A page of disabled fields tells a person what they cannot do, which
        // is not what a settings page is for.
        <div className="subgreet">
          <div className="section-eyebrow">Your settings</div>
          <div className="lede">How and when this workspace notifies you.</div>
        </div>
      )}

      {isCxo ? <SenderSection workspaceId={workspaceId} /> : null}
      {isCxo ? (
        <div className="play-meta" data-testid="settings-xi-socket" style={{ margin: "12px 0" }}>
          <span>XI socket: off. WorkForceOS runs inside your own workspace; nothing connects out to Xamun.</span>
        </div>
      ) : null}
      {isCxo ? <OrganisationSection workspaceId={workspaceId} canEdit={isCxo} /> : null}
      {isCxo ? <ReportDistributionSection workspaceId={workspaceId} canEdit={isCxo} /> : null}
      <NotificationPreferencesSection workspaceId={workspaceId} />
      {isCxo && <TeamsSection workspaceId={workspaceId} />}
    </div>
  );
}

function SettingsErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  return (
    <div className="main-inner" data-testid="settings-error">
      <div className="greet-row">
        <span className="greet">Settings unavailable</span>
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">ERROR</div>
        <div className="lede">{error.message}</div>
      </div>
      <div style={{ marginTop: 16 }}>
        <button
          type="button"
          className="btn primary"
          onClick={resetErrorBoundary}
        >
          Reload
        </button>
      </div>
    </div>
  );
}

const EVENT_LABELS: Array<{ key: keyof PerEventToggles; label: string; sub: string }> = [
  {
    key: "decision_needed",
    label: "Judgement fork",
    sub: "A routine reached a decision that needs a person.",
  },
  {
    key: "agent_failure",
    label: "Agent failure",
    sub: "An agent run failed validation and needs manual handling.",
  },
];
// replan_ready and kr_target_hit are gone with the features behind them —
// DEL-PL removed replans, and Key Results were XI's, removed with DEL-RM.
// The stored preference keys are left alone: they are data, and a migration
// to drop two booleans from a jsonb column earns nothing.

function NotificationPreferencesSection({ workspaceId }: { workspaceId: string }) {
  // Two scopes: global (default for the user across all workspaces) and
  // workspace-specific (overrides global for this workspace only). The
  // current UX shows workspace-specific by default since Settings lives
  // inside a workspace; users can switch to global via the toggle.
  const [scope, setScope] = useState<"workspace" | "global">("workspace");
  const effectiveWsId = scope === "workspace" ? workspaceId : null;
  const prefsQ = useNotificationPrefs(effectiveWsId);
  const updateMut = useUpdateNotificationPrefs();

  const prefs = prefsQ.data?.prefs;
  const [draftCadence, setDraftCadence] = useState<NotificationDigestCadence>("daily");
  const [draftEvents, setDraftEvents] = useState<PerEventToggles>({
    decision_needed: true,
    replan_ready: true,
    agent_failure: true,
    kr_target_hit: false,
  });

  // Sync draft to server response when it lands or scope changes.
  useEffect(() => {
    if (prefs) {
      setDraftCadence(prefs.digest_cadence);
      setDraftEvents(prefs.per_event);
    }
  }, [prefs?.digest_cadence, prefs?.per_event, scope]);

  const dirty =
    !!prefs &&
    (draftCadence !== prefs.digest_cadence ||
      JSON.stringify(draftEvents) !== JSON.stringify(prefs.per_event));

  function handleSave() {
    if (!dirty) return;
    updateMut.mutate({
      digest_cadence: draftCadence,
      per_event: draftEvents,
      workspace_id: effectiveWsId,
    });
  }

  return (
    <>
      <div className="section-h">
        <span className="ttl">Notification preferences</span>
        <span className="meta" data-testid="settings-notif-scope-label">
          {scope === "workspace" ? "this workspace" : "global default"}
        </span>
      </div>
      <div
        data-testid="settings-notif"
        style={{
          padding: 16,
          border: "1px solid var(--border)",
          borderRadius: 8,
          background: "white",
          fontSize: 13,
        }}
      >
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          <button
            type="button"
            data-testid="settings-notif-scope-workspace"
            className={`btn ${scope === "workspace" ? "primary" : ""}`}
            onClick={() => setScope("workspace")}
          >
            This workspace
          </button>
          <button
            type="button"
            data-testid="settings-notif-scope-global"
            className={`btn ${scope === "global" ? "primary" : ""}`}
            onClick={() => setScope("global")}
          >
            Global default
          </button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "var(--text-2)",
              marginBottom: 6,
            }}
          >
            Digest cadence
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            {(["off", "daily", "weekly"] as NotificationDigestCadence[]).map((c) => (
              <label
                key={c}
                style={{ display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer" }}
              >
                <input
                  type="radio"
                  name="digest-cadence"
                  value={c}
                  data-testid={`settings-notif-cadence-${c}`}
                  checked={draftCadence === c}
                  onChange={() => setDraftCadence(c)}
                />
                <span style={{ textTransform: "capitalize" }}>
                  {c === "off" ? "Off" : c === "daily" ? "Daily" : "Weekly"}
                </span>
              </label>
            ))}
          </div>
          <div style={{ color: "var(--text-3)", marginTop: 4 }}>
            Off = no digests; per-event toggles below still fire as real-time
            notifications. Daily/weekly bundle the same events into one
            scheduled summary.
          </div>
        </div>

        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--text-2)",
            marginBottom: 6,
          }}
        >
          Per-event notifications
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {EVENT_LABELS.map((e) => (
            <label
              key={e.key}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                padding: "6px 0",
                borderBottom: "1px solid var(--border)",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                data-testid={`settings-notif-event-${e.key}`}
                checked={draftEvents[e.key]}
                onChange={(ev) =>
                  setDraftEvents((prev) => ({ ...prev, [e.key]: ev.target.checked }))
                }
                style={{ marginTop: 2 }}
              />
              <div>
                <div style={{ fontWeight: 500 }}>{e.label}</div>
                <div style={{ color: "var(--text-3)" }}>{e.sub}</div>
              </div>
            </label>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 14 }}>
          <button
            type="button"
            className="btn primary"
            data-testid="settings-notif-save"
            onClick={handleSave}
            disabled={!dirty || updateMut.isPending}
          >
            {updateMut.isPending ? "Saving…" : "Save preferences"}
          </button>
          {updateMut.isSuccess && !dirty ? (
            <span
              data-testid="settings-notif-saved" style={{ color: "var(--accent)" }}
            >
              Saved.
            </span>
          ) : null}
          {updateMut.isError ? (
            <span
              data-testid="settings-notif-error" style={{ color: "var(--warn)" }}
            >
              {updateMut.error instanceof Error
                ? updateMut.error.message
                : "Save failed"}
            </span>
          ) : null}
        </div>
      </div>
    </>
  );
}

/**
 * How this workspace's outbound email signs itself.
 *
 * The address is the deployment's and cannot be changed here, and the page
 * says why rather than offering a field that silently does nothing: the
 * envelope address belongs to the authenticated SMTP account, and most
 * providers rewrite or reject a From that is not it. The name and the
 * reply-to are honoured everywhere, and those are what a recipient reads and
 * replies to.
 */
function SenderSection({ workspaceId }: { workspaceId: string }) {
  const qc = useQueryClient();
  const q = useQuery<
    { sender_name: string | null; reply_to_email: string | null; sending_address: string | null },
    Error
  >({
    queryKey: ["sender", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => apiGet(`/api/workspaces/${workspaceId}/sender`),
  });
  const [name, setName] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [testTo, setTestTo] = useState("");

  const shownName = name ?? q.data?.sender_name ?? "";
  const shownReply = replyTo ?? q.data?.reply_to_email ?? "";

  const save = useMutation({
    mutationFn: () =>
      apiPut(`/api/workspaces/${workspaceId}/sender`, {
        sender_name: shownName.trim() || null,
        reply_to_email: shownReply.trim() || null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sender", workspaceId] }),
  });
  const test = useMutation({
    mutationFn: () => apiPost(`/api/workspaces/${workspaceId}/sender/test`, { to: testTo.trim() }),
  });

  return (
    <>
      <div className="section-h">
        <span className="ttl">Outbound email</span>
        <span className="meta">
          {q.data?.sending_address ? `sends from ${q.data.sending_address}` : "no SMTP configured"}
        </span>
      </div>
      <div
        data-testid="settings-sender" style={{ border: "1px solid var(--border)", borderRadius: 8, background: "white" }}
      >
        <div style={{ color: "var(--text-3)", marginBottom: 10 }}>
          Reports arrive with a short brief in the body and the pack attached.
          The name below is what a recipient sees in the From line, and a reply
          goes wherever you point it.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <label style={{ color: "var(--text-3)" }}>
            From name{" "}
            <input
              aria-label="Sender name"
              placeholder="National Bonds — Digital"
              value={shownName}
              onChange={(e) => setName(e.target.value)} style={{ minWidth: 200 }}
            />
          </label>
          <label style={{ color: "var(--text-3)" }}>
            Replies go to{" "}
            <input
              aria-label="Reply-to address"
              placeholder="digital@nationalbonds.ae"
              value={shownReply}
              onChange={(e) => setReplyTo(e.target.value)} style={{ minWidth: 220 }}
            />
          </label>
          <button
            type="button"
            className="btn primary"
            data-testid="save-sender"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
          {save.isError ? (
            <span className="pill red">{apiErrorMessage(save.error)}</span>
          ) : null}
          {save.isSuccess ? (
            <span style={{ color: "var(--accent)" }}>Saved.</span>
          ) : null}
        </div>

        <div style={{ color: "var(--text-3)", marginTop: 10 }}>
          The address itself —{" "}
          <strong>{q.data?.sending_address ?? "not configured"}</strong> — belongs
          to the mail account this deployment authenticates with, so it is set
          alongside the SMTP credentials rather than here. A provider will
          rewrite or reject a From address that is not its own, which is why
          there is no field for it.
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
          <input
            aria-label="Send a test to"
            placeholder="Send a test to…"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)} style={{ minWidth: 220 }}
          />
          <button
            type="button"
            className="btn"
            data-testid="send-test-email"
            disabled={test.isPending || !testTo.includes("@")}
            onClick={() => test.mutate()}
          >
            {test.isPending ? "Sending…" : "Send a test"}
          </button>
          {test.isSuccess ? (
            <span style={{ color: "var(--accent)" }}>Sent. Check the From line.</span>
          ) : null}
          {test.isError ? (
            <span className="pill red">{apiErrorMessage(test.error)}</span>
          ) : null}
        </div>
      </div>
    </>
  );
}

interface OrgPerson {
  id: string;
  name: string;
  email: string;
  title: string | null;
  reports_to: string | null;
}

/**
 * The organisation: who the people are, what they are called, and who they
 * report to.
 *
 * A membership is a login, and most of the people a report is for do not
 * have one — a Head of Savings who reads the pack on a phone, the Head of
 * Digital the whole model reports to. Nothing recorded them, so an agent
 * could not answer to anyone and a distribution list was a set of addresses
 * with no faces behind them.
 */
function OrganisationSection({
  workspaceId,
  canEdit,
}: {
  workspaceId: string;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const q = useQuery<{ people: OrgPerson[] }, Error>({
    queryKey: ["org-people", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => apiGet(`/api/workspaces/${workspaceId}/org/people`),
  });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [reportsTo, setReportsTo] = useState("");

  const add = useMutation({
    mutationFn: () =>
      apiPost(`/api/workspaces/${workspaceId}/org/people`, {
        name,
        email,
        title: title || undefined,
        reports_to: reportsTo || null,
      }),
    onSuccess: () => {
      setName("");
      setEmail("");
      setTitle("");
      setReportsTo("");
      void qc.invalidateQueries({ queryKey: ["org-people", workspaceId] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/workspaces/${workspaceId}/org/people/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org-people", workspaceId] }),
  });

  const people = q.data?.people ?? [];
  const nameOf = (id: string | null): string =>
    people.find((p) => p.id === id)?.name ?? "—";

  return (
    <>
      <div className="section-h">
        <span className="ttl">Organisation</span>
        <span className="meta">{people.length} people</span>
      </div>
      <div
        data-testid="settings-organisation" style={{ border: "1px solid var(--border)", borderRadius: 8, background: "white" }}
      >
        <div style={{ color: "var(--text-3)", marginBottom: 10 }}>
          The people this department reports to and works for. They do not need
          a login: an agent answers to someone here, and a report goes to
          someone here.
        </div>
        {people.length === 0 ? (
          <div className="play-meta">
            <span>Nobody yet. Add the Head of Digital first, then everyone who reports to them.</span>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--text-3)" }}>
                <th style={{ padding: "4px 8px 4px 0" }}>Name</th>
                <th>Title</th>
                <th>Email</th>
                <th>Reports to</th>
                {canEdit ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {people.map((p) => (
                <tr key={p.id} data-testid={`person-${p.id}`} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ padding: "6px 8px 6px 0" }}>{p.name}</td>
                  <td style={{ color: "var(--text-3)" }}>{p.title ?? "—"}</td>
                  <td style={{ color: "var(--text-3)" }}>{p.email}</td>
                  <td style={{ color: "var(--text-3)" }}>{nameOf(p.reports_to)}</td>
                  {canEdit ? (
                    <td style={{ padding: "6px 0", textAlign: "right" }}>
                      <button
                        type="button"
                        className="btn"
                        data-testid={`remove-person-${p.id}`}
                        onClick={() => remove.mutate(p.id)}
                      >
                        remove
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {canEdit ? (
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <input
              aria-label="Person name"
              placeholder="Full name"
              value={name}
              onChange={(e) => setName(e.target.value)} style={{ minWidth: 160 }}
            />
            <input
              aria-label="Person title"
              placeholder="Head of Savings"
              value={title}
              onChange={(e) => setTitle(e.target.value)} style={{ minWidth: 160 }}
            />
            <input
              aria-label="Person email"
              placeholder="name@nationalbonds.ae"
              value={email}
              onChange={(e) => setEmail(e.target.value)} style={{ minWidth: 200 }}
            />
            <select
              aria-label="Reports to"
              value={reportsTo}
              onChange={(e) => setReportsTo(e.target.value)}
            >
              <option value="">Reports to nobody here</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn primary"
              data-testid="add-person"
              disabled={add.isPending || !name.trim() || !email.includes("@")}
              onClick={() => add.mutate()}
            >
              Add
            </button>
            {add.isError ? (
              <span className="pill red">{apiErrorMessage(add.error)}</span>
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  );
}

/**
 * Who receives each report that leaves the building.
 *
 * The same lists live on each decision's card in Agent telemetry, which is
 * where they belong when you are looking at that decision — and nowhere near
 * where anyone goes looking for "who gets the email". They are here too,
 * against the reports themselves.
 */
function ReportDistributionSection({
  workspaceId,
  canEdit,
}: {
  workspaceId: string;
  canEdit: boolean;
}) {
  const q = useQuery<{ agents: TelemetryAgent[] }, Error>({
    queryKey: ["autonomy", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => apiGet(`/api/workspaces/${workspaceId}/autonomy`),
  });
  const agents = q.data?.agents ?? [];
  return (
    <>
      <div className="section-h">
        <span className="ttl">Report distribution</span>
        <span className="meta">Executives who receive each report by email</span>
      </div>
      <div
        data-testid="settings-report-distribution" style={{ border: "1px solid var(--border)", borderRadius: 8, background: "white" }}
      >
        <div style={{ color: "var(--text-3)", marginBottom: 8 }}>
          An address here receives the report as a PowerPoint pack with a short
          brief in the body. They do not need an account. Until someone is
          named, each report goes to the workspace's supervisors.
        </div>
        {q.isLoading ? <div className="play-meta"><span>Loading the reports…</span></div> : null}
        {agents.map((agent) =>
          agent.decisions.map((d) => (
            <div key={`${agent.hand_key}:${d.key}`} style={{ marginBottom: 14 }}>
              <div style={{ fontWeight: 600 }}>{d.label}</div>
              <div style={{ color: "var(--text-3)" }}>{agent.name}</div>
              <ReportRecipients
                workspaceId={workspaceId}
                handKey={agent.hand_key}
                decisionKey={d.key}
                decisionLabel={d.label}
                canEdit={canEdit}
                heading="RECIPIENTS"
              />
            </div>
          )),
        )}
      </div>
    </>
  );
}

interface TelemetryAgent {
  hand_key: string;
  name: string;
  decisions: { key: string; label: string }[];
}

function TeamsSection({ workspaceId }: { workspaceId: string }) {
  const webhookQ = useTeamsWebhook(workspaceId);
  const updateMut = useUpdateTeamsWebhook(workspaceId);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (webhookQ.data?.webhook_url) setDraft(webhookQ.data.webhook_url);
  }, [webhookQ.data?.webhook_url]);

  const saved = webhookQ.data?.webhook_url ?? null;
  const dirty = draft.trim() !== (saved ?? "");

  return (
    <>
      <div className="section-h">
        <span className="ttl">Microsoft Teams notifications</span>
        <span className="meta">{saved ? "configured" : "not configured"}</span>
      </div>
      <div data-testid="settings-teams" style={{ border: "1px solid var(--border)", borderRadius: 8, background: "white" }}>
        <div style={{ color: "var(--text-3)", marginBottom: 8 }}>
          Paste an <strong>Incoming Webhook</strong> URL from a Teams channel to send notifications there.
        </div>
        <input
          type="url"
          placeholder="https://outlook.office.com/webhook/..."
          value={draft}
          onChange={(e) => setDraft(e.target.value)} style={{ width: "100%", borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg-soft)", color: "var(--text)", boxSizing: "border-box", marginBottom: 10 }}
        />
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            type="button"
            className="btn primary"
            disabled={!dirty || !draft.trim() || updateMut.isPending}
            onClick={() => updateMut.mutate(draft.trim())}
          >
            {updateMut.isPending ? "Saving…" : "Save"}
          </button>
          {saved && (
            <button type="button" className="btn" onClick={() => { setDraft(""); updateMut.mutate(""); }}>
              Remove
            </button>
          )}
          {updateMut.isSuccess && !dirty && <span style={{ color: "var(--accent)" }}>Saved.</span>}
        </div>
      </div>
    </>
  );
}

export function Settings() {
  return (
    <ErrorBoundary FallbackComponent={SettingsErrorFallback}>
      <SettingsInner />
    </ErrorBoundary>
  );
}
